import { boldLightGrayBgLeft, left, leftBold } from "src/app/util/excelStyles";
import { formats, getCell, withCellStyles } from "../../../utils";
import { Rows } from "../../../interfaces";
import { FollowOnPortfolioDTO } from "src/app/domain/follow-on/component/dto/FollowOnPortfolioDTO";
import { FollowOnModelDtoForExcel } from "../FollowOnModelTabletemplate";
import { boldLightGrayBg } from "../../financial-statement/capital-account/styles";
import { dashInsteadOfNull } from "src/app/services/excel/templates/pp-by-fund/mapping";
import { getSumForSubTotal } from "../mapping";
import { boldGrayBgLeft } from "../../comparison/mapping";

export const getPreparedData = (data: FollowOnModelDtoForExcel) => {
  const newData: Rows = [];
  const active = data.data.active;
  const realized = data.data.realized;
  newData.push(data.upperHeaders);
  const row = [];
  data.headersForDisplay.forEach((header) => (header === '' ? row.push(null) : row.push(withCellStyles(header, boldLightGrayBg))));
  newData.push(row);
  if (active.models.length) {
    const nameActiveRow = [];
    nameActiveRow.push(withCellStyles('Active', leftBold));
    newData.push(nameActiveRow);
    const activeRows = getActiveOrRealizedRows(active.models, 'active', data.headersForLogic);
    newData.push(...activeRows);
    const subTotalActive = getSubTotalRows(data.headersForLogic, 'Active', 4);
    newData.push(...subTotalActive);
    // set marker of total active
    activeTotalRowIndex = currentRowIndex;
    currentRowIndex++;
  }
  if (realized.models.length) {
    const nameRealizedRow = [];
    nameRealizedRow.push(withCellStyles('Realized', leftBold));
    newData.push(nameRealizedRow);
    currentRowIndex++;
    const realizedRows = getActiveOrRealizedRows(realized.models, 'realized', data.headersForLogic);
    newData.push(...realizedRows);
    // 4 here start row and + 2 need to sum for row totalActive and row realized
    const startRowForRealizedSubTotal = active.models?.length ? 4 + active.models?.length + 2 : 4;
    const subTotalRealized = getSubTotalRows(data.headersForLogic, 'Realized', startRowForRealizedSubTotal);
    newData.push(...subTotalRealized);
    // set marker for total realized
    realizedTotalRowIndex = currentRowIndex;
    currentRowIndex++;
  }
  if (active.models?.length && realized.models?.length) {
    const grandTotalRow = getGrandTotalRow(data.headersForLogic);
    newData.push(...grandTotalRow);
  }
  resetRowIndexes();
  return newData;
};

const getActiveOrRealizedRows = (models: FollowOnPortfolioDTO[], status: string, headers: string[]) => {
  const rows: Rows = [];
  const mappedToHeaders = models.map((model) => getNewRoundMapping(model));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], left));
    }
    rows.push(row);
  }
  return rows;
};

const getNewRoundMapping = (model: FollowOnPortfolioDTO) => {
  const roundFirst = model.rounds[0];
  const roundSecond = model.rounds[1];
  const roundThird = model.rounds[2];
  const body = {
    'Company': model.name,
    'Investment': getCell({
      v: dashInsteadOfNull(model.currentCost),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Current comp.value': getCell({
      v: dashInsteadOfNull(model.currentCompValue),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null': null,

    'Round date': getCell({
      v: dashInsteadOfNullDate(roundFirst.roundDate),
      z: formats.date,
      t: 'n',
    }),
    'Post-money': getCell({
      v: dashInsteadOfNull(roundFirst.postMoney),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value': getCell({
      v: dashInsteadOfNull(roundFirst.exitValue),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability': getCell({
      v: dashInsteadOfNull(roundFirst.exitProbability),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple': getCell({
      v: dashInsteadOfNull(roundFirst.multiple),
      z: formats.multiplier,
      t: 'n',
    }),
    'Risk prob. (1-10)': getCell({
      v: dashInsteadOfNull(roundFirst.riskProb),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation': getCell({
      v: dashInsteadOfNull(roundFirst.cost),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null(0)': null,

    'Round date(0)': getCell({
      v: dashInsteadOfNullDate(roundSecond.roundDate),
      z: formats.date,
      t: 'n',
    }),
    'Post-money(0)': getCell({
      v: dashInsteadOfNull(roundSecond.postMoney),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value(0)': getCell({
      v: dashInsteadOfNull(roundSecond.exitValue),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability(0)': getCell({
      v: dashInsteadOfNull(roundSecond.exitProbability),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple(0)': getCell({
      v: dashInsteadOfNull(roundSecond.multiple),
      z: formats.multiplier,
      t: 'n',
    }),
    'Risk prob. (1-10)(0)': getCell({
      v: dashInsteadOfNull(roundSecond.riskProb),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation(0)': getCell({
      v: dashInsteadOfNull(roundSecond.cost),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null(1)': null,

    'Round date(1)': getCell({
      v: dashInsteadOfNullDate(roundThird.roundDate),
      z: formats.date,
      t: 'n',
    }),
    'Post-money(1)': getCell({
      v: dashInsteadOfNull(roundThird.postMoney),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value(1)': getCell({
      v: dashInsteadOfNull(roundThird.exitValue),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability(1)': getCell({
      v: dashInsteadOfNull(roundThird.exitProbability),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple(1)': getCell({
      v: dashInsteadOfNull(roundThird.multiple),
      z: formats.multiplier,
      t: 'n',
    }),
    'Risk prob. (1-10)(1)': getCell({
      v: dashInsteadOfNull(roundThird.riskProb),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation(1)': getCell({
      v: dashInsteadOfNull(roundThird.cost),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  }
  currentRowIndex++;
  return body
};

const getSubTotalRows = (headers: string[], status: string, startRowIndex: number) => {
  const rows: Rows = [];
  const mappedToHeaders = getSubTotalMapping(status, startRowIndex)
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getSubTotalMapping = (status: string, startRow: number) => {
  const body = {
    'Company': `Total ${status}`,
    'Investment': getCell({
      f: getSumForSubTotal('B', startRow, (currentRowIndex - 1)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Current comp.value': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null': null,

    'Round date': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Post-money': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Risk prob. (1-10)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation': getCell({
      f: getSumForSubTotal('k', startRow, (currentRowIndex - 1)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null(0)': null,

    'Round date(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Post-money(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Risk prob. (1-10)(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation(0)': getCell({
      f: getSumForSubTotal('S', startRow, (currentRowIndex - 1)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null(1)': null,

    'Round date(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Post-money(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Risk prob. (1-10)(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation(1)': getCell({
      f: getSumForSubTotal('AA', startRow, (currentRowIndex - 1)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  }
  return [body];
}

const getGrandTotalRow = (headers: string[]) => {
  const rows: Rows = [];
  const mappedToHeaders = getGrandTotalMapping();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;

};

const getGrandTotalMapping = () => {
  const body = {
    'Company': `Grand total`,
    'Investment': getCell({
      f: getSumForGrandTotal('B'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Current comp.value': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null': null,

    'Round date': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Post-money': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Risk prob. (1-10)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation': getCell({
      f: getSumForGrandTotal('k'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null(0)': null,

    'Round date(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Post-money(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Risk prob. (1-10)(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation(0)': getCell({
      f: getSumForGrandTotal('S'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'null(1)': null,

    'Round date(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Post-money(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit value(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Exit probability(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Potential multiple(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Risk prob. (1-10)(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Investment allocation(1)': getCell({
      f: getSumForGrandTotal('AA'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  }
  return [body];
}



let currentRowIndex = 4;
let activeTotalRowIndex: number;
let realizedTotalRowIndex: number;

const resetRowIndexes = () => {
  currentRowIndex = 4;
  activeTotalRowIndex = undefined;
  realizedTotalRowIndex = undefined;
};

const getSumForGrandTotal = (letter: string) => {
  return `SUM(${letter}${activeTotalRowIndex},${letter}${realizedTotalRowIndex})`;
};

const dashInsteadOfNullDate = (value: any) => {
  return value ? value : '';
}

